import { useEffect, useRef, useState } from "react";

const stats = [
  { value: 200, suffix: "K+", label: "Candidates Engaged" },
  { value: 5000, suffix: "+", label: "Roles Filled" },
  { value: 1000, suffix: "+", label: "Talent Leaders & Founders Served" },
  { value: 10, suffix: "M+", label: "AI Markers of Talent Intelligence" },
];

function useCountUp(target: number, shouldStart: boolean, duration = 2000) {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (!shouldStart) return;
    let startTime: number | null = null;
    let raf: number;

    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCurrent(Math.round(eased * target));
      if (progress < 1) raf = requestAnimationFrame(animate);
    };

    raf = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf);
  }, [shouldStart, target, duration]);

  return current;
}

const StatItem = ({
  value,
  suffix,
  label,
  started,
  index,
}: {
  value: number;
  suffix: string;
  label: string;
  started: boolean;
  index: number;
}) => {
  const count = useCountUp(value, started);

  return (
    <div
      className="group flex flex-col items-center gap-1.5 px-6 py-6 transition-all duration-700"
      style={{
        opacity: started ? 1 : 0,
        transform: started ? "translateY(0)" : "translateY(20px)",
        transitionDelay: `${index * 150}ms`,
      }}
    >
      <span className="bg-gradient-to-r from-app-300 to-white bg-clip-text font-serif text-xl tracking-tight text-transparent md:text-3xl lg:text-[2.75rem]">
        {count.toLocaleString("en-IN")}
        {suffix}
      </span>
      <span className="max-w-[200px] text-center text-xs font-medium tracking-wider text-gray-400 md:text-sm">
        {label}
      </span>
    </div>
  );
};

const StatsRibbon = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={ref}
      className="relative w-full overflow-hidden border-y border-white/5 py-2"
      style={{
        background:
          "radial-gradient(ellipse 80% 60% at 50% 50%, rgba(99, 205, 220, 0.04), transparent)",
      }}
    >
      <div className="mx-auto flex max-w-screen-xl flex-wrap justify-center divide-x divide-white/10 md:flex-nowrap">
        {stats.map((stat, i) => (
          <StatItem key={i} {...stat} index={i} started={visible} />
        ))}
      </div>
    </section>
  );
};

export default StatsRibbon;
